/* eslint-disable react/no-unescaped-entities */
/**
 * OrientationPanel.jsx — Score de maturité + suggestion de spécialistes.
 *
 * Affiche :
 *  - une jauge VERT / ORANGE / ROUGE selon le niveau du dossier ;
 *  - les raisons détectées par le moteur déterministe ;
 *  - les CTA de spécialistes recommandés (avocat, commissaire de justice, etc.) ;
 *  - des actions globales (rappel, analyse approfondie).
 *
 * Charte stricte (`/app/memory/NEGOCIATION_CHARTER.md`) :
 *  - JAMAIS de qualification juridique. Toujours « orientation indicative ».
 */
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { emitExpertLead, trackExpertLead, pingBackendLead } from "@/lib/negociationTracking";
import {
  Shield, AlertTriangle, AlertCircle, ArrowRight, Phone, FileSearch,
  Sparkles, CheckCircle2, Briefcase,
} from "lucide-react";

const LEVEL_THEME = {
  autonomy: {
    color: "#16a34a", bg: "#dcfce7", border: "#86efac",
    Icon: Shield, badge: "🟢 Vert", short: "Dossier amiable",
  },
  reinforced: {
    color: "#d97706", bg: "#ffedd5", border: "#fdba74",
    Icon: AlertTriangle, badge: "🟠 Orange", short: "Assistance professionnelle conseillée",
  },
  professional: {
    color: "#dc2626", bg: "#fee2e2", border: "#fca5a5",
    Icon: AlertCircle, badge: "🔴 Rouge", short: "Intervention rapide recommandée",
  },
};

export default function OrientationPanel({ orientation, caseId, onTransmit }) {
  if (!orientation) return null;
  const { level, level_info, recommendations = [], signals = [], global_actions = [], disclaimer, stats } = orientation;
  const theme = LEVEL_THEME[level] || LEVEL_THEME.autonomy;
  const { Icon } = theme;

  return (
    <section className="rounded-2xl bg-white border border-slate-200 overflow-hidden mb-6" data-testid="orientation-panel">
      {/* HEADER — Score de maturité */}
      <div className="px-6 py-5 border-b border-slate-200 flex items-start gap-4"
           style={{ background: theme.bg, borderLeft: `4px solid ${theme.color}` }}>
        <div className="w-11 h-11 rounded-full flex items-center justify-center shrink-0"
             style={{ background: "white", border: `2px solid ${theme.color}` }}>
          <Icon className="w-5 h-5" style={{ color: theme.color }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium"
                  style={{ background: "white", color: theme.color, border: `1px solid ${theme.border}` }}
                  data-testid="maturity-badge">
              Score de maturité : {theme.badge}
            </span>
            <span className="text-[11px] uppercase tracking-wider text-slate-500">
              Niveau {level_info?.rank} · {level_info?.label}
            </span>
          </div>
          <p className="mt-2 font-serif text-lg" style={{ color: theme.color }} data-testid="maturity-title">
            {level_info?.title}
          </p>
          <p className="mt-1 text-sm text-slate-700 leading-relaxed">
            {level_info?.description}
          </p>
        </div>
      </div>

      {/* SIGNALS détectés */}
      {signals.length > 0 && (
        <div className="px-6 py-4 bg-slate-50 border-b border-slate-200">
          <p className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">Signaux détectés</p>
          <ul className="space-y-1.5">
            {signals.map(s => (
              <li key={s.key} className="text-xs text-slate-700 flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[#C9A961] mt-1.5 shrink-0" />
                <span><b>{s.label} —</b> {s.detail}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* RECOMMENDATIONS — Spécialistes proposés */}
      {recommendations.length > 0 && (
        <div className="px-6 py-5">
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#C9A961] font-medium mb-1 flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5" /> Spécialistes CONFILEX adaptés à votre dossier
          </p>
          {level === "professional" && (
            <p className="text-xs text-slate-600 mb-3 leading-relaxed">
              Votre dossier nécessite probablement l'intervention d'un professionnel.
              CONFILEX vous propose plusieurs solutions complémentaires — vous pouvez en activer
              une ou plusieurs.
            </p>
          )}
          <div className={`grid gap-3 ${recommendations.length >= 3 ? "lg:grid-cols-3 md:grid-cols-2" : "md:grid-cols-2"}`}>
            {recommendations.map(r => (
              <div key={r.profile}
                   className="rounded-xl border border-[#C9A961]/40 bg-[#FAF7F1] p-4 flex flex-col"
                   data-testid={`reco-${r.profile}`}>
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-[#0B3A2A] flex items-center justify-center shrink-0">
                    <Briefcase className="w-4 h-4 text-[#C9A961]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-serif text-base text-[#0B3A2A]">{r.label}</p>
                      {r.pertinence_score != null && (
                        <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-[#0B3A2A] text-white shrink-0"
                              title="Indice de pertinence calculé par CONFILEX"
                              data-testid={`reco-score-${r.profile}`}>
                          {r.pertinence_score}%
                        </span>
                      )}
                    </div>
                    {r.services && r.services.length > 0 && (
                      <ul className="mt-2 space-y-0.5">
                        {r.services.map((sv, i) => (
                          <li key={i} className="text-[11px] text-slate-600 leading-snug flex items-start gap-1">
                            <span className="text-[#C9A961] mt-0.5">•</span> {sv}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
                {r.reasons && r.reasons.length > 0 && r.reasons[0] !== r.services?.[0] && (
                  <p className="mt-2 text-[11px] text-slate-500 italic leading-snug">
                    {r.reasons[0]}
                  </p>
                )}
                <div className="mt-auto pt-3 flex flex-wrap gap-2">
                  <Button asChild size="sm" className="bg-[#0B3A2A] hover:bg-[#0B3A2A] text-white"
                          data-testid={`btn-cta-${r.profile}`}>
                    <Link
                      to={emitExpertLead({
                        route: r.route, caseId, profile: r.profile, level,
                        pertinence: r.pertinence_score, action: "cta_click",
                      })}
                      onClick={() => {
                        // re-track au clic (le emitExpertLead initial sert pour l'URL)
                        trackExpertLead({ caseId, profile: r.profile, level,
                                          pertinence: r.pertinence_score, action: "cta_click" });
                        pingBackendLead({ caseId, profile: r.profile, level,
                                          pertinence: r.pertinence_score, action: "cta_click" });
                      }}
                    >
                      {r.cta_label} <ArrowRight className="w-3.5 h-3.5 ml-1" />
                    </Link>
                  </Button>
                  <Button size="sm" variant="outline"
                          onClick={() => {
                            trackExpertLead({ caseId, profile: r.profile, level,
                                              pertinence: r.pertinence_score, action: "transmit_click" });
                            onTransmit && onTransmit(r.profile, r.pertinence_score);
                          }}
                          data-testid={`btn-transmit-${r.profile}`}>
                    <FileSearch className="w-3.5 h-3.5 mr-1" /> Transmettre
                  </Button>
                </div>
              </div>
            ))}
          </div>
          {level === "professional" && recommendations.length >= 2 && (
            <p className="mt-4 text-[11px] text-slate-500 italic leading-relaxed">
              <Sparkles className="inline w-3 h-3 text-[#C9A961] mr-1" />
              Une approche combinée (par exemple <b>avocat + commissaire de justice</b> ou
              <b> expert-comptable + avocat</b>) peut être pertinente. Vous pouvez activer
              plusieurs profils sur un même dossier — vos pièces et historique seront
              automatiquement transmis à chacun.
            </p>
          )}
        </div>
      )}

      {/* ACTIONS GLOBALES */}
      {global_actions.length > 0 && (
        <div className="px-6 py-4 bg-[#FAFAF7] border-t border-slate-200 flex flex-wrap items-center gap-2">
          <span className="text-[11px] uppercase tracking-wider text-slate-500 mr-2">Autres actions :</span>
          {global_actions.map(a => (
            <Button key={a.key} asChild variant="outline" size="sm" data-testid={`btn-global-${a.key}`}>
              <Link to={a.route}>
                {a.key === "callback" ? <Phone className="w-3.5 h-3.5 mr-1" /> : <FileSearch className="w-3.5 h-3.5 mr-1" />}
                {a.label}
              </Link>
            </Button>
          ))}
        </div>
      )}

      {/* DISCLAIMER */}
      <div className="px-6 py-3 bg-slate-50 border-t border-slate-200 flex items-start gap-2">
        <CheckCircle2 className="w-3.5 h-3.5 text-slate-400 mt-0.5 shrink-0" />
        <p className="text-[11px] text-slate-500 leading-relaxed italic">{disclaimer}</p>
      </div>
    </section>
  );
}
